// GrowthQuizPage.js — Student Holistic Growth Intelligence Quiz
// Tabs: Available Quizzes | Take Quiz | Results | Growth Metrics

import React, { useEffect, useState, useRef } from "react";
import Layout from "../Layout";
import { Spin, message } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import katex from "katex";
import "katex/dist/katex.min.css";
import {
    BookOpen, Clock, ChevronLeft, ChevronRight, CheckCircle, XCircle,
    Award, ArrowLeft, BarChart3, FileText,
    Send, Timer, AlertCircle, Hourglass
} from "lucide-react";
import { api } from "../authClient";
import "./GrowthQuizPage.css";

// ─── Constants ───
const QUESTION_TYPE_LABELS = {
    mcq: "MCQ",
    scenario_mcq: "Scenario",
    true_false_explain: "True / False",
    open_ended: "Open Ended",
    case_based: "Case Study",
    decision_tradeoff: "Decision",
};

const QUESTION_TYPE_CLASS = {
    mcq: "gq-type-mcq",
    scenario_mcq: "gq-type-scenario",
    true_false_explain: "gq-type-truefalse",
    open_ended: "gq-type-openended",
    case_based: "gq-type-casebased",
    decision_tradeoff: "gq-type-decision",
};

const DOMAIN_COLORS = {
    academics: "#1565c0",
    skills: "#7b1fa2",
    competencies: "#00838f",
    health: "#2e7d32",
    career: "#e65100",
    tech: "#283593",
    values: "#c62828",
};

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];

// ─── Math Renderer ───
// Renders a string that may contain LaTeX delimiters: \(...\), \[...\], $...$, $$...$$
const renderMath = (text) => {
    if (!text) return text;
    const str = String(text);

    // Split on LaTeX delimiters, preserving them in the result
    const parts = str.split(/(\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|\$\$[\s\S]*?\$\$|\$[^$\n]+?\$)/g);

    if (parts.length === 1) return str; // no LaTeX found — return plain string

    return (
        <span>
            {parts.map((part, i) => {
                // Display math: \[...\] or $$...$$
                if (/^\\\[[\s\S]*?\\\]$/.test(part)) {
                    const tex = part.slice(2, -2).trim();
                    try {
                        return (
                            <span
                                key={i}
                                dangerouslySetInnerHTML={{
                                    __html: katex.renderToString(tex, { displayMode: true, throwOnError: false }),
                                }}
                            />
                        );
                    } catch { return <span key={i}>{part}</span>; }
                }
                if (/^\$\$[\s\S]*?\$\$$/.test(part)) {
                    const tex = part.slice(2, -2).trim();
                    try {
                        return (
                            <span
                                key={i}
                                dangerouslySetInnerHTML={{
                                    __html: katex.renderToString(tex, { displayMode: true, throwOnError: false }),
                                }}
                            />
                        );
                    } catch { return <span key={i}>{part}</span>; }
                }
                // Inline math: \(...\) or $...$
                if (/^\\\([\s\S]*?\\\)$/.test(part)) {
                    const tex = part.slice(2, -2).trim();
                    try {
                        return (
                            <span
                                key={i}
                                dangerouslySetInnerHTML={{
                                    __html: katex.renderToString(tex, { displayMode: false, throwOnError: false }),
                                }}
                            />
                        );
                    } catch { return <span key={i}>{part}</span>; }
                }
                if (/^\$[^$\n]+?\$$/.test(part)) {
                    const tex = part.slice(1, -1).trim();
                    try {
                        return (
                            <span
                                key={i}
                                dangerouslySetInnerHTML={{
                                    __html: katex.renderToString(tex, { displayMode: false, throwOnError: false }),
                                }}
                            />
                        );
                    } catch { return <span key={i}>{part}</span>; }
                }
                // Plain text
                return <span key={i}>{part}</span>;
            })}
        </span>
    );
};

// ─── Main Component ───
const GrowthQuizPage = () => {
    const [activeTab, setActiveTab] = useState("available");
    const [loading, setLoading] = useState(false);

    // Student profile + courses (books)
    const [studentGrade, setStudentGrade]               = useState(null);
    const [courses, setCourses]                         = useState([]);
    const [loadingCourses, setLoadingCourses]           = useState(false);
    const [selectedCourse, setSelectedCourse]           = useState(null); // { book_id, name, ... }

    // Available quizzes
    const [quizzes, setQuizzes] = useState([]);

    // Taking quiz
    const [quizData, setQuizData] = useState(null); // from /take
    const [currentIdx, setCurrentIdx] = useState(0);
    const [answers, setAnswers] = useState({}); // { questionIndex: { type, value, ... } }
    const [quizTimer, setQuizTimer] = useState(0);
    const [quizStarted, setQuizStarted] = useState(false);
    const timerRef = useRef(null);

    // Results
    const [resultsList, setResultsList] = useState([]);
    const [currentResult, setCurrentResult] = useState(null); // after submit or viewing history
    const [resultEvidence, setResultEvidence] = useState([]);

    // My Learnings quiz results
    const [myLearningsResults, setMyLearningsResults] = useState([]);
    const [myLearningsLoading, setMyLearningsLoading] = useState(false);

    // Metrics summary
    const [metricsSummary, setMetricsSummary] = useState(null);

    // ─── Fetch student grade + courses on mount ───
    useEffect(() => {
        api.get("/student/user/me").then(res => {
            setStudentGrade(res.data.grade || null);
        }).catch(() => {});
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        setLoadingCourses(true);
        try {
            const res = await api.get("/books");
            setCourses(res.data.books || []);
        } catch {
            setCourses([]);
        }
        setLoadingCourses(false);
    };

    // ─── Fetch on tab switch ───
    useEffect(() => {
        if (activeTab === "available" && selectedCourse) fetchQuizzes();
        if (activeTab === "results") fetchResults();
        if (activeTab === "my-learnings-results") fetchMyLearningsResults();
        if (activeTab === "metrics") fetchMetrics();
    }, [activeTab]);

    // ─── Fetch quizzes whenever course selection changes ───
    useEffect(() => {
        if (selectedCourse) {
            fetchQuizzes();
        } else {
            setQuizzes([]);
        }
    }, [selectedCourse]);

    const fetchQuizzes = async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedCourse) params.subject = selectedCourse.name;
            const res = await api.get("/student/growth-quizzes/available", { params });
            setQuizzes(res.data.quizzes || []);
        } catch (err) {
            message.error("Failed to load quizzes");
        }
        setLoading(false);
    };

    const fetchResults = async () => {
        setLoading(true);
        try {
            const res = await api.get("/student/growth-quizzes/results");
            setResultsList(res.data.results || []);
        } catch (err) {
            message.error("Failed to load results");
        }
        setLoading(false);
    };

    const fetchMyLearningsResults = async () => {
        setMyLearningsLoading(true);
        try {
            const res = await api.get("/student/growth-quizzes/my-learnings-results");
            setMyLearningsResults(res.data.results || []);
        } catch {
            message.error("Failed to load My Learnings results");
        }
        setMyLearningsLoading(false);
    };

    const fetchMetrics = async () => {
        setLoading(true);
        try {
            const res = await api.get("/student/growth-metrics/summary", { params: { months: 3 } });
            setMetricsSummary(res.data);
        } catch (err) {
            message.error("Failed to load metrics");
        }
        setLoading(false);
    };

    // ─── Start a quiz ───
    const startQuiz = async (quizId) => {
        setLoading(true);
        try {
            const res = await api.get(`/student/growth-quizzes/${quizId}/take`);
            setQuizData(res.data);
            setCurrentIdx(0);
            setAnswers({});
            setQuizTimer((res.data.time_limit_minutes || 15) * 60);
            setQuizStarted(true);
            setCurrentResult(null);
            setActiveTab("taking");
        } catch (err) {
            const detail = err.response?.data?.detail || "Failed to start quiz";
            message.error(detail);
        }
        setLoading(false);
    };

    // ─── Timer ───
    useEffect(() => {
        if (activeTab === "taking" && quizStarted && quizTimer > 0) {
            timerRef.current = setInterval(() => {
                setQuizTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        handleSubmitQuiz();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timerRef.current);
        }
    }, [activeTab, quizStarted]);

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    // ─── Answer handlers ───
    const setAnswer = (idx, data) => {
        setAnswers(prev => ({ ...prev, [idx]: data }));
    };

    const handleMCQSelect = (idx, option) => {
        setAnswer(idx, { question_type: "mcq", selected_option: option });
    };

    const handleTFSelect = (idx, boolVal) => {
        const existing = answers[idx] || {};
        setAnswer(idx, { ...existing, question_type: "true_false_explain", bool_answer: boolVal });
    };

    const handleTFExplanation = (idx, text) => {
        const existing = answers[idx] || {};
        setAnswer(idx, { ...existing, question_type: "true_false_explain", explanation: text });
    };

    const handleTextAnswer = (idx, text, qType) => {
        setAnswer(idx, { question_type: qType, text_answer: text });
    };

    // ─── Submit quiz ───
    const handleSubmitQuiz = async () => {
        if (!quizData) return;
        setLoading(true);
        clearInterval(timerRef.current);

        const formattedAnswers = quizData.questions.map((q, i) => {
            const ans = answers[i] || {};
            return {
                question_index: i,
                question_type: q.question_type,
                selected_option: ans.selected_option || null,
                text_answer: ans.text_answer || null,
                bool_answer: ans.bool_answer !== undefined ? ans.bool_answer : null,
                explanation: ans.explanation || null,
            };
        });

        try {
            const res = await api.post(`/student/growth-quizzes/${quizData.quiz_id}/submit`, {
                answers: formattedAnswers,
            });
            setCurrentResult(res.data);
            setQuizStarted(false);
            setActiveTab("result-view");
            message.success("Quiz submitted!");
        } catch (err) {
            const detail = err.response?.data?.detail || "Failed to submit quiz";
            message.error(detail);
        }
        setLoading(false);
    };

    // ─── View result detail ───
    const viewResultDetail = async (quizId) => {
        setLoading(true);
        try {
            const res = await api.get(`/student/growth-quizzes/${quizId}/result`);
            setCurrentResult(res.data.result);
            setResultEvidence(res.data.evidence || []);
            setActiveTab("result-view");
        } catch (err) {
            message.error("Failed to load result details");
        }
        setLoading(false);
    };

    // ─── Navigation ───
    const goNext = () => {
        if (quizData && currentIdx < quizData.questions.length - 1) {
            setCurrentIdx(prev => prev + 1);
        }
    };

    const goPrev = () => {
        if (currentIdx > 0) setCurrentIdx(prev => prev - 1);
    };

    // ─── Tab content renderers ───

    // Course card color palette
    const COURSE_COLORS_LIST = [
        "#D67C2C", "#1565c0", "#2e7d32", "#7b1fa2",
        "#00838f", "#c62828", "#283593", "#e65100",
    ];

    // ── Available Quizzes Tab ──
    const renderAvailableQuizzes = () => {
        // ── Step 1: Course selection ──
        if (!selectedCourse) {
            return (
                <div>
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "Poppins, sans-serif" }}>
                            Select a Course
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">Choose a course to see its growth quizzes</p>
                    </div>

                    {loadingCourses ? (
                        <div className="gq-loading-center"><Spin size="large" /></div>
                    ) : courses.length === 0 ? (
                        <div className="gq-empty-state">
                            <BookOpen size={48} color="#ccc" />
                            <h3>No courses found</h3>
                            <p>You are not enrolled in any courses yet.</p>
                        </div>
                    ) : (
                        <motion.div
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                            initial="hidden"
                            animate="show"
                            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
                        >
                            {courses.map((course, idx) => {
                                const color = COURSE_COLORS_LIST[idx % COURSE_COLORS_LIST.length];
                                return (
                                    <motion.div
                                        key={course.book_id}
                                        variants={{ hidden: { opacity: 0, y: 20, scale: 0.95 }, show: { opacity: 1, y: 0, scale: 1 } }}
                                        whileHover={{ y: -6, boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}
                                        className="bg-white border-2 rounded-xl overflow-hidden shadow-md transition-all duration-300 cursor-pointer"
                                        style={{ borderColor: color }}
                                        onClick={() => setSelectedCourse(course)}
                                    >
                                        <div className="h-2" style={{ background: color }} />
                                        <div className="p-6">
                                            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `${color}18` }}>
                                                <BookOpen size={22} style={{ color }} />
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-800 mb-4 min-h-[3rem] line-clamp-2">
                                                {course.name}
                                            </h3>
                                            <button
                                                className="w-full py-2.5 px-4 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
                                                style={{ background: color }}
                                                onClick={() => setSelectedCourse(course)}
                                            >
                                                <span>View Quizzes</span>
                                                <ArrowLeft size={14} style={{ transform: "rotate(180deg)" }} />
                                            </button>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}
                </div>
            );
        }

        // ── Step 2: Quiz list for selected course ──
        return (
            <div>
                {/* Back button + course header */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                        onClick={() => setSelectedCourse(null)}
                    >
                        <ArrowLeft size={16} />
                        Back to Courses
                    </button>
                    <span className="text-gray-300">|</span>
                    <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: "Poppins, sans-serif" }}>
                        {selectedCourse.name}
                    </h2>
                    {studentGrade && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white">
                            Grade {studentGrade}
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="gq-loading-center"><Spin size="large" /></div>
                ) : quizzes.length === 0 ? (
                    <div className="gq-empty-state">
                        <BookOpen size={48} color="#ccc" />
                        <h3>No quizzes available</h3>
                        <p>No growth quizzes have been assigned for {selectedCourse.name} yet.</p>
                    </div>
                ) : (
                    <motion.div
                        className="gq-quiz-grid"
                        initial="hidden"
                        animate="show"
                        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
                    >
                        {quizzes.map((quiz) => (
                            <motion.div
                                key={quiz._id}
                                className={`gq-quiz-card ${quiz.attempted ? "attempted" : ""}`}
                                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                                onClick={() => startQuiz(quiz._id)}
                            >
                                <div className="gq-quiz-card-header">
                                    <div>
                                        <h3>{quiz.topic || "Growth Quiz"}</h3>
                                        <div className="gq-quiz-card-meta">
                                            {quiz.grade   && <span className="gq-tag gq-tag-grade">Grade {quiz.grade}</span>}
                                            {quiz.subject && <span className="gq-tag gq-tag-subject">{quiz.subject}</span>}
                                            {quiz.attempted ? (
                                                <span className="gq-tag gq-tag-attempted">Attempted</span>
                                            ) : (
                                                <span className="gq-tag gq-tag-new">New</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="gq-quiz-card-footer">
                                    <span>{quiz.total_questions || "?"} questions</span>
                                    <span>{quiz.created_at ? new Date(quiz.created_at).toLocaleDateString() : ""}</span>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>
        );
    };

    // ── Taking Quiz Tab ──
    const renderTakingQuiz = () => {
        if (!quizData) return null;
        const questions = quizData.questions;
        const q = questions[currentIdx];
        const answeredCount = Object.keys(answers).length;
        const progress = ((currentIdx + 1) / questions.length) * 100;

        return (
            <div className="gq-taking-container">
                {/* Header */}
                <div className="gq-taking-header">
                    <div>
                        <strong style={{ fontSize: 16 }}>{quizData.topic || "Growth Quiz"}</strong>
                        <div style={{ fontSize: 13, color: "#888" }}>
                            {answeredCount}/{questions.length} answered
                        </div>
                    </div>
                    <div className={`gq-timer ${quizTimer < 60 ? "warning" : ""}`}>
                        <Timer size={18} />
                        {formatTime(quizTimer)}
                    </div>
                </div>

                {/* Progress */}
                <div className="gq-progress-bar">
                    <div className="gq-progress-fill" style={{ width: `${progress}%` }} />
                </div>

                {/* Question Navigator */}
                <div className="gq-question-nav">
                    {questions.map((_, i) => (
                        <div
                            key={i}
                            className={`gq-dot ${i === currentIdx ? "current" : ""} ${answers[i] ? "answered" : ""}`}
                            onClick={() => setCurrentIdx(i)}
                        >
                            {i + 1}
                        </div>
                    ))}
                </div>

                {/* Question Card */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentIdx}
                        className="gq-question-card"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -30 }}
                        transition={{ duration: 0.25 }}
                    >
                        {/* Meta badges */}
                        <div className="gq-question-meta">
                            <span className={`gq-question-type-badge ${QUESTION_TYPE_CLASS[q.question_type] || ""}`}>
                                {QUESTION_TYPE_LABELS[q.question_type] || q.question_type}
                            </span>
                            {q.domain && (
                                <span className="gq-domain-badge" style={{ borderLeft: `3px solid ${DOMAIN_COLORS[q.domain] || "#888"}` }}>
                                    {q.domain}
                                    {q.sub_metric ? ` / ${q.sub_metric}` : ""}
                                </span>
                            )}
                        </div>

                        <div className="gq-question-number">Question {currentIdx + 1} of {questions.length}</div>
                        <div className="gq-question-text">{renderMath(q.question)}</div>

                        {/* Render answer input based on question type */}
                        {renderQuestionInput(q, currentIdx)}
                    </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                <div className="gq-nav-buttons">
                    <button className="gq-nav-btn gq-nav-btn-prev" onClick={goPrev} disabled={currentIdx === 0}>
                        <ChevronLeft size={18} /> Previous
                    </button>

                    {currentIdx === questions.length - 1 ? (
                        <button className="gq-nav-btn gq-nav-btn-submit" onClick={handleSubmitQuiz} disabled={loading}>
                            <Send size={18} /> Submit Quiz
                        </button>
                    ) : (
                        <button className="gq-nav-btn gq-nav-btn-next" onClick={goNext}>
                            Next <ChevronRight size={18} />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // ── Render question input by type ──
    const renderQuestionInput = (q, idx) => {
        const currentAnswer = answers[idx] || {};

        switch (q.question_type) {
            case "mcq":
            case "scenario_mcq":
                return (
                    <div className="gq-options">
                        {(q.options || []).map((opt, oi) => (
                            <div
                                key={oi}
                                className={`gq-option ${currentAnswer.selected_option === opt ? "selected" : ""}`}
                                onClick={() => handleMCQSelect(idx, opt)}
                            >
                                <span className="gq-option-letter">{OPTION_LETTERS[oi]}</span>
                                <span>{renderMath(opt)}</span>
                            </div>
                        ))}
                    </div>
                );

            case "true_false_explain":
                return (
                    <div>
                        <div className="gq-tf-options">
                            <div
                                className={`gq-tf-btn ${currentAnswer.bool_answer === true ? "selected-true" : ""}`}
                                onClick={() => handleTFSelect(idx, true)}
                            >
                                <CheckCircle size={20} style={{ marginBottom: 4 }} />
                                <div>True</div>
                            </div>
                            <div
                                className={`gq-tf-btn ${currentAnswer.bool_answer === false ? "selected-false" : ""}`}
                                onClick={() => handleTFSelect(idx, false)}
                            >
                                <XCircle size={20} style={{ marginBottom: 4 }} />
                                <div>False</div>
                            </div>
                        </div>
                        <textarea
                            className="gq-text-answer"
                            placeholder="Explain your reasoning (optional)..."
                            value={currentAnswer.explanation || ""}
                            onChange={e => handleTFExplanation(idx, e.target.value)}
                            rows={3}
                        />
                    </div>
                );

            case "open_ended":
            case "decision_tradeoff":
                return (
                    <div>
                        <textarea
                            className="gq-text-answer"
                            placeholder={q.question_type === "decision_tradeoff"
                                ? "Explain your decision and reasoning..."
                                : "Write your answer here..."
                            }
                            value={currentAnswer.text_answer || ""}
                            onChange={e => handleTextAnswer(idx, e.target.value, q.question_type)}
                            rows={5}
                        />
                        <div className="gq-text-answer-hint">
                            {q.auto_scorable === false && "This answer will be reviewed by your teacher."}
                        </div>
                    </div>
                );

            case "case_based":
                return (
                    <div>
                        <textarea
                            className="gq-text-answer"
                            placeholder="Analyze the case and write your response..."
                            value={currentAnswer.text_answer || ""}
                            onChange={e => handleTextAnswer(idx, e.target.value, "case_based")}
                            rows={5}
                        />
                        {q.sub_questions && q.sub_questions.length > 0 && (
                            <div className="gq-sub-questions">
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#888", marginBottom: 8 }}>Sub-questions (answer in your main response):</div>
                                {q.sub_questions.map((sq, si) => (
                                    <div key={si} className="gq-sub-question">
                                        <div className="gq-sub-question-text">{si + 1}. {renderMath(sq.question)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="gq-text-answer-hint">This answer will be reviewed by your teacher.</div>
                    </div>
                );

            default:
                return (
                    <textarea
                        className="gq-text-answer"
                        placeholder="Write your answer..."
                        value={currentAnswer.text_answer || ""}
                        onChange={e => handleTextAnswer(idx, e.target.value, q.question_type)}
                        rows={4}
                    />
                );
        }
    };

    // ── Result View (after submit or from history) ──
    const renderResultView = () => {
        if (!currentResult) return null;

        const autoScore    = currentResult.auto_score    || {};
        const finalScore   = currentResult.final_score   || null;
        const feedback     = currentResult.feedback      || [];
        const pendingCount = currentResult.pending_review_count ?? 0;
        const isPartial    = pendingCount > 0;

        // Split feedback into auto-scored and pending buckets
        const autoFeedback    = feedback.filter(fb => fb.status !== "pending_review");
        const pendingFeedback = feedback.filter(fb => fb.status === "pending_review");

        // Score values for the complete view
        const displayPct = finalScore
            ? finalScore.percentage
            : (autoScore.percentage || 0);
        const scoreColor = displayPct >= 80 ? "#4caf50"
            : displayPct >= 60 ? "#1565c0"
            : displayPct >= 40 ? "#ff9800"
            : "#f44336";

        return (
            <div className="gq-results-container">
                <button className="gq-back-btn" onClick={() => { setActiveTab("available"); setCurrentResult(null); }}>
                    <ArrowLeft size={16} /> Back to Quizzes
                </button>

                {/* ── PARTIAL RESULT HERO ── */}
                {isPartial ? (
                    <motion.div
                        className="gq-score-hero"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", duration: 0.6 }}
                        style={{ borderTop: "4px solid #ff9800" }}
                    >
                        {/* Pending clock icon instead of a score circle */}
                        <div style={{
                            width: 80, height: 80, borderRadius: "50%",
                            background: "#fff3e0", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            margin: "0 auto 16px",
                        }}>
                            <Hourglass size={36} style={{ color: "#ff9800" }} />
                        </div>
                        <h2 style={{ color: "#e65100" }}>Quiz Submitted!</h2>
                        <p style={{ fontWeight: 600, color: "#555" }}>
                            Your answers are in. Part of your score will be available after your teacher reviews your written answers.
                        </p>

                        {/* Two-column stats */}
                        <div style={{
                            display: "flex", gap: 16, justifyContent: "center",
                            flexWrap: "wrap", marginTop: 20,
                        }}>
                            {/* Auto-scored */}
                            <div style={{
                                background: "#f1f8e9", border: "1px solid #aed581",
                                borderRadius: 12, padding: "14px 24px", textAlign: "center", minWidth: 140,
                            }}>
                                <div style={{ fontSize: 26, fontWeight: 700, color: "#558b2f" }}>
                                    {autoScore.correct || 0}/{autoScore.total_auto ?? autoScore.total ?? 0}
                                </div>
                                <div style={{ fontSize: 13, color: "#689f38", marginTop: 4 }}>
                                    Auto-scored correct
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 600, color: "#33691e", marginTop: 4 }}>
                                    {autoScore.percentage || 0}%
                                </div>
                            </div>
                            {/* Pending review */}
                            <div style={{
                                background: "#fff8e1", border: "1px solid #ffe082",
                                borderRadius: 12, padding: "14px 24px", textAlign: "center", minWidth: 140,
                            }}>
                                <div style={{ fontSize: 26, fontWeight: 700, color: "#f57f17" }}>
                                    {pendingCount}
                                </div>
                                <div style={{ fontSize: 13, color: "#f9a825", marginTop: 4 }}>
                                    Awaiting teacher review
                                </div>
                                <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                                    Final score updates when done
                                </div>
                            </div>
                        </div>

                        {/* Info notice */}
                        <div style={{
                            display: "flex", alignItems: "flex-start", gap: 10,
                            background: "#fff3e0", border: "1px solid #ffcc02",
                            borderRadius: 10, padding: "12px 16px",
                            marginTop: 20, textAlign: "left",
                        }}>
                            <AlertCircle size={18} style={{ color: "#e65100", flexShrink: 0, marginTop: 1 }} />
                            <span style={{ fontSize: 13, color: "#5d4037", lineHeight: 1.5 }}>
                                Your <strong>{pendingCount} open-ended / written answer{pendingCount > 1 ? "s" : ""}</strong> will
                                be reviewed by your teacher. Your final score and full feedback will appear here once the review is complete.
                                You can check back in <em>My Results</em>.
                            </span>
                        </div>
                    </motion.div>
                ) : (
                    /* ── COMPLETE RESULT HERO ── */
                    <motion.div
                        className="gq-score-hero"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", duration: 0.6 }}
                    >
                        <div className="gq-score-circle" style={{ background: scoreColor }}>
                            {displayPct}%
                            <span className="gq-score-label">
                                {finalScore ? "Final Score" : "Score"}
                            </span>
                        </div>
                        <h2>
                            {displayPct >= 80 ? "Excellent Work!" :
                             displayPct >= 60 ? "Good Job!" :
                             displayPct >= 40 ? "Keep Practicing!" :
                             "Keep Learning!"}
                        </h2>
                        <p>
                            {finalScore
                                ? `${finalScore.earned} / ${finalScore.possible} points across all questions`
                                : `${autoScore.correct || 0} of ${autoScore.total_auto ?? autoScore.total ?? 0} questions correct`
                            }
                        </p>
                    </motion.div>
                )}

                {/* ── AUTO-SCORED FEEDBACK ── */}
                {autoFeedback.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, color: "#1a1a2e", display: "flex", alignItems: "center", gap: 8 }}>
                            <CheckCircle size={16} style={{ color: "#4caf50" }} />
                            Auto-Scored Questions
                        </h3>
                        <div className="gq-feedback-list">
                            {autoFeedback.map((fb, i) => (
                                <motion.div
                                    key={i}
                                    className={`gq-feedback-item ${fb.is_correct === true ? "correct" : "incorrect"}`}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.04 }}
                                >
                                    <div className="gq-feedback-question">
                                        Q{fb.index + 1} — {QUESTION_TYPE_LABELS[fb.question_type] || fb.question_type}
                                    </div>
                                    <div className="gq-feedback-detail">
                                        {fb.is_correct === true && (
                                            <span style={{ color: "#4caf50" }}>
                                                <CheckCircle size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                                                Correct!
                                            </span>
                                        )}
                                        {fb.is_correct === false && (
                                            <span style={{ color: "#f44336" }}>
                                                <XCircle size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                                                Incorrect.{" "}
                                                {fb.correct_answer ? `Answer: ${fb.correct_answer}` : ""}
                                                {fb.correct_bool !== undefined ? `Answer: ${fb.correct_bool ? "True" : "False"}` : ""}
                                            </span>
                                        )}
                                        {/* Teacher feedback when result is complete */}
                                        {fb.teacher_feedback && (
                                            <span style={{ color: "#1565c0", marginLeft: 8, fontSize: 12 }}>
                                                Teacher: {fb.teacher_feedback}
                                            </span>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── PENDING / TEACHER-REVIEWED QUESTIONS ── */}
                {pendingFeedback.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, color: "#1a1a2e", display: "flex", alignItems: "center", gap: 8 }}>
                            {isPartial
                                ? <><Clock size={16} style={{ color: "#ff9800" }} /> Pending Teacher Review</>
                                : <><CheckCircle size={16} style={{ color: "#1565c0" }} /> Teacher-Reviewed Questions</>
                            }
                        </h3>
                        <div className="gq-feedback-list">
                            {pendingFeedback.map((fb, i) => (
                                <motion.div
                                    key={i}
                                    className="gq-feedback-item pending"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.04 }}
                                >
                                    <div className="gq-feedback-question">
                                        Q{fb.index + 1} — {QUESTION_TYPE_LABELS[fb.question_type] || fb.question_type}
                                    </div>
                                    <div className="gq-feedback-detail">
                                        {isPartial ? (
                                            <span style={{ color: "#ff9800" }}>
                                                <Clock size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                                                Awaiting teacher review
                                            </span>
                                        ) : (
                                            <>
                                                <span style={{ color: fb.is_correct ? "#4caf50" : "#f44336" }}>
                                                    {fb.is_correct
                                                        ? <><CheckCircle size={14} style={{ verticalAlign: "middle", marginRight: 4 }} /> Good answer</>
                                                        : <><XCircle size={14} style={{ verticalAlign: "middle", marginRight: 4 }} /> Needs improvement</>
                                                    }
                                                    {fb.score !== undefined && fb.score !== null && ` (${fb.score}/1)`}
                                                </span>
                                                {fb.teacher_feedback && (
                                                    <div style={{ marginTop: 4, fontSize: 12, color: "#1565c0" }}>
                                                        Teacher feedback: {fb.teacher_feedback}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Evidence trail (only shown when viewing history, not after immediate submission) */}
                {resultEvidence.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, color: "#1a1a2e" }}>Evidence Trail</h3>
                        <div className="gq-feedback-list">
                            {resultEvidence.map((ev, i) => (
                                <div key={i} className={`gq-feedback-item ${ev.is_correct === true ? "correct" : ev.is_correct === false ? "incorrect" : "pending"}`}>
                                    <div className="gq-feedback-question">
                                        {ev.domain} / {ev.sub_metric}
                                    </div>
                                    <div className="gq-feedback-detail">
                                        {renderMath(ev.question)}
                                        {" | "}
                                        {ev.status === "pending_review"
                                            ? <span style={{ color: "#ff9800" }}>Pending review</span>
                                            : <span>Score: {ev.score !== null && ev.score !== undefined ? ev.score : "—"}</span>
                                        }
                                        {ev.teacher_feedback && (
                                            <span style={{ color: "#1565c0", marginLeft: 8, fontSize: 12 }}>
                                                Teacher: {ev.teacher_feedback}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ── Results History Tab ──
    const renderResultsHistory = () => (
        <div>
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-6">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                        <Award size={16} style={{ color: "white" }} />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900" style={{ fontFamily: "Poppins, sans-serif" }}>
                            My Quiz Results
                        </span>
                        {studentGrade && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white">
                                Grade {studentGrade}
                            </span>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-16"><Spin size="large" /></div>
                ) : resultsList.length === 0 ? (
                    <div className="text-center py-16">
                        <FileText size={48} className="mx-auto mb-3 text-gray-300" style={{ color: "#d1d5db" }} />
                        <h3 className="text-lg font-medium text-gray-600 mb-1">No results yet</h3>
                        <p className="text-sm text-gray-400">Complete a growth quiz to see your results here.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {["Topic", "Subject", "Grade", "Score", "Status", "Date"].map(h => (
                                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {resultsList.map(r => {
                                    const autoScore = r.auto_score || {};
                                    const pct = autoScore.percentage || 0;
                                    const scoreColorClass = pct >= 80 ? "text-green-600" : pct >= 60 ? "text-blue-600" : pct >= 40 ? "text-amber-600" : "text-red-500";
                                    return (
                                        <tr
                                            key={r._id}
                                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                                            onClick={() => viewResultDetail(r.quiz_id)}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-gray-900 text-sm">{r.topic || "Growth Quiz"}</div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">{r.subject || "—"}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {r.grade && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        Grade {r.grade}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {r.status === "partial" ? (
                                                    <div>
                                                        <span className={`text-base font-bold ${scoreColorClass}`}>{pct}%</span>
                                                        <span className="text-xs text-gray-400 ml-1">auto</span>
                                                        <div className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                                                            <Clock size={11} /> Final score pending
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {r.final_score ? (
                                                            <>
                                                                <span className={`text-lg font-bold ${scoreColorClass}`}>{r.final_score.percentage}%</span>
                                                                <span className="text-xs text-gray-400 ml-1">final</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className={`text-lg font-bold ${scoreColorClass}`}>{pct}%</span>
                                                                <span className="text-xs text-gray-400 ml-1">({autoScore.correct || 0}/{autoScore.total_auto || 0})</span>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    r.status === "partial"
                                                        ? "bg-amber-100 text-amber-800"
                                                        : "bg-green-100 text-green-800"
                                                }`}>
                                                    {r.status === "partial"
                                                        ? <><Hourglass size={10} /> Pending Review</>
                                                        : <><CheckCircle size={10} /> Complete</>
                                                    }
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    // ── Growth Metrics Tab ──
    const renderMetrics = () => {
        if (loading) return <div className="gq-loading-center"><Spin size="large" /></div>;
        if (!metricsSummary || !metricsSummary.domains || Object.keys(metricsSummary.domains).length === 0) {
            return (
                <div className="text-center py-16">
                    <BarChart3 size={48} className="mx-auto mb-3" style={{ color: "#d1d5db" }} />
                    <h3 className="text-lg font-medium text-gray-600 mb-1">No growth data yet</h3>
                    <p className="text-sm text-gray-400">Complete quizzes to see your growth metrics across all domains.</p>
                </div>
            );
        }

        const domains = metricsSummary.domains;

        return (
            <div>
                <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-6">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <BarChart3 size={16} style={{ color: "white" }} />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900" style={{ fontFamily: "Poppins, sans-serif" }}>
                                    My Growth Metrics
                                </span>
                                {studentGrade && (
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white">
                                        Grade {studentGrade}
                                    </span>
                                )}
                            </div>
                        </div>
                        <span className="text-sm text-gray-500">Last {metricsSummary.period_months} months</span>
                    </div>
                </div>

                <div className="gq-metrics-grid">
                    {Object.entries(domains).map(([key, domain]) => {
                        const color = DOMAIN_COLORS[key] || "#888";
                        return (
                            <motion.div
                                key={key}
                                className={`gq-domain-card gq-domain-${key}`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <h3 style={{ color }}>{domain.label || key}</h3>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 14 }}>
                                    <span>Accuracy: <strong>{domain.accuracy || 0}%</strong></span>
                                    <span>Level: <strong>{domain.level?.label || domain.level || "—"}</strong></span>
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <div style={{ height: 8, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
                                        <div style={{
                                            height: "100%", width: `${domain.accuracy || 0}%`,
                                            background: color, borderRadius: 4, transition: "width 0.5s ease"
                                        }} />
                                    </div>
                                </div>
                                {domain.sub_metrics && Object.entries(domain.sub_metrics).map(([smKey, sm]) => (
                                    <div key={smKey} className="gq-sub-metric-row">
                                        <span style={{ color: "#555" }}>{smKey}</span>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color }}>{sm.accuracy}%</span>
                                            <div className="gq-sub-metric-bar">
                                                <div className="gq-sub-metric-fill" style={{ width: `${sm.accuracy}%`, background: color }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div style={{ marginTop: 8, fontSize: 12, color: "#aaa" }}>
                                    {domain.total_questions} questions answered
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ─── My Learnings Results Tab ───
    const renderMyLearningsResults = () => (
        <div>
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-6">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                        <BookOpen size={16} style={{ color: "white" }} />
                    </div>
                    <div>
                        <span className="font-semibold text-gray-900" style={{ fontFamily: "Poppins, sans-serif" }}>
                            My Learnings Quiz Results
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">Quizzes submitted from the My Learnings section</p>
                    </div>
                </div>

                {myLearningsLoading ? (
                    <div className="flex justify-center items-center py-16"><Spin size="large" /></div>
                ) : myLearningsResults.length === 0 ? (
                    <div className="text-center py-16">
                        <FileText size={48} className="mx-auto mb-3" style={{ color: "#d1d5db" }} />
                        <h3 className="text-lg font-medium text-gray-600 mb-1">No results yet</h3>
                        <p className="text-sm text-gray-400">Complete a quiz in My Learnings to see results here.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {["Topic", "Subject", "Questions", "Score", "Status", "Submitted"].map(h => (
                                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {myLearningsResults.map(r => {
                                    const autoScore = r.auto_score || {};
                                    const autoPct = autoScore.percentage || 0;
                                    const scoreColor = autoPct >= 80 ? "text-green-600" : autoPct >= 60 ? "text-blue-600" : autoPct >= 40 ? "text-amber-600" : "text-red-500";
                                    return (
                                        <tr key={r._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-gray-900 text-sm">{r.topic || "My Learnings Quiz"}</div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">{r.subject || "—"}</td>
                                            <td className="px-6 py-4 text-sm text-gray-500">{r.total_questions || "—"}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {r.review_complete ? (
                                                    <div>
                                                        <span className={`text-lg font-bold ${scoreColor}`}>{r.final_score?.percentage ?? autoPct}%</span>
                                                        <span className="text-xs text-gray-400 ml-1">final</span>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {autoScore.total > 0 ? (
                                                            <>
                                                                <span className={`text-base font-bold ${scoreColor}`}>{autoPct}%</span>
                                                                <span className="text-xs text-gray-400 ml-1">auto ({autoScore.correct}/{autoScore.total})</span>
                                                            </>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">Pending</span>
                                                        )}
                                                        {r.pending_review_count > 0 && (
                                                            <div className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                                                                <Clock size={11} /> {r.pending_review_count} awaiting teacher review
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    r.review_complete
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-amber-100 text-amber-800"
                                                }`}>
                                                    {r.review_complete
                                                        ? <><CheckCircle size={10} /> Review Complete</>
                                                        : <><Hourglass size={10} /> Pending Review</>
                                                    }
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    // ─── Main Render ───
    return (
        <Layout>
            <div className="growth-quiz-container">
                {/* Page header + tabs — Tailwind, hidden during quiz/result */}
                {activeTab !== "taking" && activeTab !== "result-view" && (
                    <div className="mb-6">
                        <div className="flex items-center gap-3 mb-5">
                            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Poppins, sans-serif" }}>
                                Growth Quizzes
                            </h1>
                            {studentGrade && (
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-sm">
                                    Grade {studentGrade}
                                </span>
                            )}
                        </div>
                        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
                            {[
                                { key: "available",            label: "Quizzes",             icon: <BookOpen size={15} /> },
                                { key: "results",              label: "Results",             icon: <Award size={15} /> },
                                { key: "my-learnings-results", label: "My Learnings",        icon: <FileText size={15} /> },
                                { key: "metrics",              label: "Growth Metrics",      icon: <BarChart3 size={15} /> },
                            ].map(t => (
                                <button
                                    key={t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                        activeTab === t.key
                                            ? "bg-white shadow-sm text-[#D67C2C]"
                                            : "text-gray-600 hover:text-gray-900"
                                    }`}
                                >
                                    {t.icon} {t.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tab Content */}
                {activeTab === "available"            && renderAvailableQuizzes()}
                {activeTab === "taking"               && renderTakingQuiz()}
                {activeTab === "result-view"          && renderResultView()}
                {activeTab === "results"              && renderResultsHistory()}
                {activeTab === "my-learnings-results" && renderMyLearningsResults()}
                {activeTab === "metrics"              && renderMetrics()}
            </div>
        </Layout>
    );
};

export default GrowthQuizPage;
